/**
 * React's view of the install state: the device, the captured install dialog,
 * the `?for=` button that brought someone here, and what this device remembers
 * about the home-screen card. Each is a `useSyncExternalStore` over a plain
 * source, so the rules in `./device.ts` and `./memory.ts` stay pure.
 */
import { useSyncExternalStore } from 'react'

import { parseInstallTarget, readStandalone, type InstallTarget } from './device'
import {
  isInstallCardDone,
  markInstallCardDone,
  readVisits,
  recordVisit,
  safeLocalStorage,
  safeSessionStorage,
} from './memory'
import {
  getInstallPromptState,
  subscribeInstallPrompt,
  type InstallPromptState,
} from './prompt-store'

export interface DeviceReading {
  userAgent: string
  maxTouchPoints: number
  isStandalone: boolean
}

let cachedDevice: DeviceReading | null = null

function readDevice(): DeviceReading {
  if (cachedDevice) return cachedDevice
  let isStandaloneDisplay = false
  try {
    isStandaloneDisplay = window.matchMedia('(display-mode: standalone)').matches
  } catch {
    // No matchMedia: treat as not installed.
  }
  const nav = navigator as Navigator & { standalone?: boolean }
  cachedDevice = {
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
    isStandalone: readStandalone({
      matchesStandaloneDisplayMode: isStandaloneDisplay,
      navigatorStandalone: nav.standalone,
    }),
  }
  return cachedDevice
}

const noSubscription = () => () => {}

/** The browser's own answers about this device. They do not change while the page is open. */
export function useDeviceReading(): DeviceReading {
  return useSyncExternalStore(noSubscription, readDevice)
}

function readChosenTarget(): InstallTarget | null {
  return parseInstallTarget(new URLSearchParams(window.location.search).get('for'))
}

/** The marketing-site button that brought the visitor here (`?for=…`). */
export function useChosenInstallTarget(): InstallTarget | null {
  return useSyncExternalStore(noSubscription, readChosenTarget)
}

function readInstallAddress(): string {
  return `${window.location.host}${window.location.pathname}`
}

/** This page's address without the query, to type in on another device. */
export function useInstallAddress(): string {
  return useSyncExternalStore(noSubscription, readInstallAddress)
}

export function useInstallPromptState(): InstallPromptState {
  return useSyncExternalStore(subscribeInstallPrompt, getInstallPromptState)
}

const memoryListeners = new Set<() => void>()

function subscribeMemory(listener: () => void): () => void {
  memoryListeners.add(listener)
  return () => memoryListeners.delete(listener)
}

function notifyMemory() {
  for (const listener of memoryListeners) listener()
}

/** A primitive, so the snapshot is stable until storage actually changes. */
function readMemorySnapshot(): string {
  const local = safeLocalStorage()
  return `${readVisits(local)}:${isInstallCardDone(local) ? 1 : 0}`
}

/** Count this visit (once per browser session). */
export function countInstallVisit(): void {
  recordVisit(safeLocalStorage(), safeSessionStorage())
  notifyMemory()
}

/** Dismissed or installed: never ask again on this device. */
export function finishInstallCard(): void {
  markInstallCardDone(safeLocalStorage())
  notifyMemory()
}

export interface InstallMemory {
  visits: number
  isDone: boolean
}

/** What this device remembers about the home-screen card. */
export function useInstallMemory(): InstallMemory {
  const snapshot = useSyncExternalStore(subscribeMemory, readMemorySnapshot)
  const [visits, done] = snapshot.split(':')
  return { visits: Number(visits), isDone: done === '1' }
}
